/**
 * The school's colours and crest, set as Sprout tenant tokens on <html>.
 * Ported from featurereq lib/school-brand.ts for Vite (#358): the values come
 * from VITE_SCHOOL_* build-time env vars instead of server env.
 *
 * Three colours, darkest to lightest: primary (identity), secondary (marks) and
 * tertiary (grounds). They go on the ROOT element because the --px-accent
 * tokens resolve there. Defaults to Salesian College Sunbury's scheme (crest
 * navy, crest gold darkened to 3:1 for marks, crest gold at 20% for grounds;
 * approved 2026-10-19). School colours never go into the design-system
 * package: it serves every school.
 */
export const SCR_COLOURS = { primary: '#00234b', secondary: '#b07a00', tertiary: '#fef0d4' } as const;

export const SCR_SCHOOL_NAME = 'Salesian College Sunbury';

type BrandEnv = Partial<Record<
  | 'VITE_SCHOOL_COLOUR_PRIMARY'
  | 'VITE_SCHOOL_COLOUR_SECONDARY'
  | 'VITE_SCHOOL_COLOUR_TERTIARY'
  | 'VITE_SCHOOL_CREST_URL'
  | 'VITE_SCHOOL_NAME',
  string | undefined
>>;

const HEX = /^#[0-9a-f]{6}$/i;
const colour = (v: string | undefined) => (v && HEX.test(v.trim()) ? v.trim() : undefined);

const viteEnv = (): BrandEnv => (import.meta.env ?? {}) as BrandEnv;

export function schoolBrandStyle(env: BrandEnv = viteEnv()): Record<string, string> {
  return {
    '--px-tenant-accent': colour(env.VITE_SCHOOL_COLOUR_PRIMARY) ?? SCR_COLOURS.primary,
    '--px-tenant-accent-2': colour(env.VITE_SCHOOL_COLOUR_SECONDARY) ?? SCR_COLOURS.secondary,
    '--px-tenant-accent-3': colour(env.VITE_SCHOOL_COLOUR_TERTIARY) ?? SCR_COLOURS.tertiary,
  };
}

/** Square crest image URL, shown at 32px beside the school's name. https or same-origin only. */
export function schoolCrestUrl(env: BrandEnv = viteEnv()): string | undefined {
  const url = env.VITE_SCHOOL_CREST_URL?.trim();
  return url && /^(https:\/\/|\/)/.test(url) ? url : undefined;
}

export function schoolName(env: BrandEnv = viteEnv()): string {
  return env.VITE_SCHOOL_NAME?.trim() || SCR_SCHOOL_NAME;
}

/** Writes the tenant tokens onto <html>. Call once at start-up. */
export function applySchoolBrand(root: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(schoolBrandStyle())) {
    root.style.setProperty(name, value);
  }
}
